import React, { useEffect, useState } from "react";
import PageWrapper from "../../components/PageWrapper";
import { IPortalRouteOptions } from "../../shared/types/shared/routes/routeTypes";
import { SmsFailed } from "@mui/icons-material";
import { IPerfmonFileSubmission, PerfmonFileSubmissionState } from "../../shared/types/dispatch/IFailedPerformanceMetrics";
import { Stack } from "@mui/material";
import { FailedPerformanceOutstandingTable } from "../../components/dispatch/failed-performance/FailedPerformanceOutstandingTable";
import { PermanentlyFailedPerformanceTable } from "../../components/dispatch/failed-performance/PermanentlyFailedPerformanceTable";
import { SuccessfullySubmittedPerformanceTable } from "../../components/dispatch/failed-performance/SuccessfullySubmittedPerformanceTable";
import { useLazyDownloadPerfmonFileQuery, useGetPerfmonFileSubmissionsQuery, useUploadPerfmonFileMutation } from "../../shared/api/DispatchApi";
import { useToastAlertContext } from "../../components/ToastAlert";
import { FormatDateToString } from "../../shared/helpers/dateTimeHelper";

const FailedPerformanceFilePage: React.FC<IFailedPerformanceFilePage> = () => {
    const [failedPerfmonSubmissions, setFailedPerfmonSubmissions] = useState<IPerfmonFileSubmission[]>([])
    const [permanentlyFailedPerfmonSubmissions, setPermanentlyFailedPerfmonSubmissions] = useState<IPerfmonFileSubmission[]>([])
    const [successfulPerfmonSubmissions, setSuccessfulPerfmonSubmissions] = useState<IPerfmonFileSubmission[]>([])

    const pollingInterval = 300000 // Poll every 5 minutes
    const { data: perfmonFileSubmissions, isLoading, isFetching, refetch } = useGetPerfmonFileSubmissionsQuery(undefined, { pollingInterval })
    const [refetchScheduled, setRefetchScheduled] = useState<boolean>(false)
    const [downloadFile] = useLazyDownloadPerfmonFileQuery()
    const [uploadFile] = useUploadPerfmonFileMutation()
    const { popToast } = useToastAlertContext();

    const triggerDelayedRefetch = () => {
        if (!refetchScheduled) {
            setRefetchScheduled(true)
            setTimeout(() => {
                refetch()
                setRefetchScheduled(false)
            }, 10000);
        }
    }

    const clickDownload = async (perfmon: IPerfmonFileSubmission) => {
        var { data, isError } = await downloadFile({ id: perfmon.id })
        if (isError || !data) {
            popToast("Something went wrong when trying to download the " + FormatDateToString(perfmon.performanceMonitoringFileBegins, "DD.MM.YYYY HH:mm") + " file for " + perfmon.site, "error")
        } else {
            const url = data.url;
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", perfmon.id);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
    }

    const submitNewFile = async (existingPerfmon: IPerfmonFileSubmission, file: File) => {
        const siteID = existingPerfmon.site
        const fileStartTime = existingPerfmon.performanceMonitoringFileBegins
        const result = await uploadFile({ siteID, fileStartTime, file });
        if ('error' in result) {
            popToast("Something went wrong when resubmitting Perfmon file for " + siteID, "error")
        } else {
            popToast("Perfmon file for " + siteID + " Resubmitted", "success")
            triggerDelayedRefetch()
        }
    }

    useEffect(() => {
        if (perfmonFileSubmissions) {
            const failed = perfmonFileSubmissions.filter(submission => submission.state === PerfmonFileSubmissionState.Failed)
            const permanentlyFailed = perfmonFileSubmissions.filter(submission => submission.state === PerfmonFileSubmissionState.PermanentlyFailed)
            const successful = perfmonFileSubmissions.filter(submission => submission.state === PerfmonFileSubmissionState.Succeeded)
            setFailedPerfmonSubmissions(failed)
            setPermanentlyFailedPerfmonSubmissions(permanentlyFailed)
            setSuccessfulPerfmonSubmissions(successful)
        }
    }, [perfmonFileSubmissions])

    return (
        <PageWrapper title="Failed Performance File Submissions" loaded={!isLoading} refreshing={isFetching}>
            <Stack direction="column" spacing={1}>
                <FailedPerformanceOutstandingTable data={failedPerfmonSubmissions} onClickDownloadFile={clickDownload} submitNewFile={submitNewFile} />
                <PermanentlyFailedPerformanceTable data={permanentlyFailedPerfmonSubmissions} onClickDownloadFile={clickDownload} />
                <SuccessfullySubmittedPerformanceTable data={successfulPerfmonSubmissions} onClickDownloadFile={clickDownload} />
            </Stack>
        </PageWrapper>
    );
}

export interface IFailedPerformanceFilePage {

}

const FailedPerformanceFilePagePageConfig: IPortalRouteOptions = {
    relativeUrl: "failedPerformance",
    page: <FailedPerformanceFilePage />,
    navDisplay: {
        title: "Failed Performance",
        icon: <SmsFailed />
    }
}

export default FailedPerformanceFilePagePageConfig;